import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {IssueComment} from '@github-ui/commenting/IssueComment'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {AddedToProjectV2Event} from '@github-ui/timeline-items/AddedToProjectV2Event'
import {AssignedEvent} from '@github-ui/timeline-items/AssignedEvent'
import {BlockedByAddedEvent} from '@github-ui/timeline-items/BlockedByAddedEvent'
import {BlockedByRemovedEvent} from '@github-ui/timeline-items/BlockedByRemovedEvent'
import {BlockingAddedEvent} from '@github-ui/timeline-items/BlockingAddedEvent'
import {BlockingRemovedEvent} from '@github-ui/timeline-items/BlockingRemovedEvent'
import {ClosedEvent} from '@github-ui/timeline-items/ClosedEvent'
import {CommentDeletedEvent} from '@github-ui/timeline-items/CommentDeletedEvent'
import {ConnectedEvent} from '@github-ui/timeline-items/ConnectedEvent'
import {ConvertedFromDraftEvent} from '@github-ui/timeline-items/ConvertedFromDraftEvent'
import {ConvertedToDiscussionEvent} from '@github-ui/timeline-items/ConvertedToDiscussionEvent'
import {CrossReferencedEvent, type ReferenceTypes} from '@github-ui/timeline-items/CrossReferencedEvent'
import {DemilestonedEvent} from '@github-ui/timeline-items/DemilestonedEvent'
import {DisconnectedEvent} from '@github-ui/timeline-items/DisconnectedEvent'
import {IssueCommentPinnedEvent} from '@github-ui/timeline-items/IssueCommentPinnedEvent'
import {IssueCommentUnpinnedEvent} from '@github-ui/timeline-items/IssueCommentUnpinnedEvent'
import type {IssueEventRationales$data} from '@github-ui/timeline-items/IssueEventRationales'
import {IssueFieldAddedEvent} from '@github-ui/timeline-items/IssueFieldAddedEvent'
import {IssueFieldChangedEvent} from '@github-ui/timeline-items/IssueFieldChangedEvent'
import {IssueFieldRemovedEvent} from '@github-ui/timeline-items/IssueFieldRemovedEvent'
import {IssueTypeAddedEvent} from '@github-ui/timeline-items/IssueTypeAddedEvent'
import {IssueTypeChangedEvent} from '@github-ui/timeline-items/IssueTypeChangedEvent'
import {IssueTypeRemovedEvent} from '@github-ui/timeline-items/IssueTypeRemovedEvent'
import {LabeledEvent} from '@github-ui/timeline-items/LabeledEvent'
import {wrapElement} from '@github-ui/timeline-items/LayoutHelpers'
import {LockedEvent} from '@github-ui/timeline-items/LockedEvent'
import {MarkedAsDuplicateEvent} from '@github-ui/timeline-items/MarkedAsDuplicateEvent'
import {MentionedEvent} from '@github-ui/timeline-items/MentionedEvent'
import {MilestonedEvent} from '@github-ui/timeline-items/MilestonedEvent'
import {ParentIssueAddedEvent} from '@github-ui/timeline-items/ParentIssueAddedEvent'
import {ParentIssueRemovedEvent} from '@github-ui/timeline-items/ParentIssueRemovedEvent'
import {PinnedEvent} from '@github-ui/timeline-items/PinnedEvent'
import {ProjectV2ItemStatusChangedEvent} from '@github-ui/timeline-items/ProjectV2ItemStatusChangedEvent'
import {ReferencedEvent} from '@github-ui/timeline-items/ReferencedEvent'
import {RemovedFromProjectV2Event} from '@github-ui/timeline-items/RemovedFromProjectV2Event'
import {RenamedTitleEvent} from '@github-ui/timeline-items/RenamedTitleEvent'
import {ReopenedEvent} from '@github-ui/timeline-items/ReopenedEvent'
import type {PreloadedSecondaryQueryType} from '@github-ui/timeline-items/SecondaryTimelineQuery'
import {SubIssueAddedEvent} from '@github-ui/timeline-items/SubIssueAddedEvent'
import {SubIssueRemovedEvent} from '@github-ui/timeline-items/SubIssueRemovedEvent'
import {SubscribedEvent} from '@github-ui/timeline-items/SubscribedEvent'
import {TimelineRowBorder, type TimelineRowBorderCommentParams} from '@github-ui/timeline-items/TimelineRowBorder'
import {TransferredEvent} from '@github-ui/timeline-items/TransferredEvent'
import {UnassignedEvent} from '@github-ui/timeline-items/UnassignedEvent'
import {UnlabeledEvent} from '@github-ui/timeline-items/UnlabeledEvent'
import {UnlockedEvent} from '@github-ui/timeline-items/UnlockedEvent'
import {UnmarkedAsDuplicateEvent} from '@github-ui/timeline-items/UnmarkedAsDuplicateEvent'
import {UnpinnedEvent} from '@github-ui/timeline-items/UnpinnedEvent'
import {UnsubscribedEvent} from '@github-ui/timeline-items/UnsubscribedEvent'
import {UserBlockedEvent} from '@github-ui/timeline-items/UserBlockedEvent'
import {VALUES} from '@github-ui/timeline-items/Values'
import {Link} from '@primer/react'
import type {ReactElement, RefObject} from 'react'
import {graphql} from 'relay-runtime'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {OptionConfig} from '../OptionConfig'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import {useTimelineConnectionIds} from './TimelineConnectionContext'

/**
 * Required props to render a timeline item event component.
 */
type EventItemInnerProps = {
  queryRef: IssueTimelineItem$data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rollupGroup?: Record<string, any[]>
  currentIssueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLDivElement | null>
  referenceTypes?: ReferenceTypes
  viewer: string | null
  onLinkClick?: (event: MouseEvent) => void
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
  rationales?: Record<string, IssueEventRationales$data['eventRationales'][number]>
}

/**
 * Record of all possible timeline items that can be rendered (IssueComment is not included).
 */
const TIMELINE_ITEMS: Record<string, ((args: EventItemInnerProps) => React.ReactElement | null) | undefined> = {
  SubscribedEvent,
  UnsubscribedEvent,
  MentionedEvent,
  ClosedEvent,
  ReopenedEvent,
  RenamedTitleEvent,
  LockedEvent,
  UnlockedEvent,
  PinnedEvent,
  IssueCommentPinnedEvent,
  UnpinnedEvent,
  IssueCommentUnpinnedEvent,
  LabeledEvent,
  UnlabeledEvent,
  UnassignedEvent,
  AssignedEvent,
  BlockedByAddedEvent,
  BlockedByRemovedEvent,
  BlockingAddedEvent,
  BlockingRemovedEvent,
  CommentDeletedEvent,
  UserBlockedEvent,
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  ConnectedEvent,
  TransferredEvent,
  DisconnectedEvent,
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  ConvertedToDiscussionEvent,
  AddedToProjectV2Event,
  RemovedFromProjectV2Event,
  ProjectV2ItemStatusChangedEvent,
  ConvertedFromDraftEvent,
  SubIssueAddedEvent,
  SubIssueRemovedEvent,
  ParentIssueAddedEvent,
  ParentIssueRemovedEvent,
  IssueTypeAddedEvent,
  IssueTypeRemovedEvent,
  IssueTypeChangedEvent,
  IssueFieldAddedEvent,
  IssueFieldChangedEvent,
  IssueFieldRemovedEvent,
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TimelineItemFragment = graphql`
  fragment IssueTimelineItem on IssueTimelineItems @inline {
    __id
    __typename
    ... on TimelineEvent {
      databaseId
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      createdAt
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      actor {
        login
      }
    }
    ... on IssueComment {
      databaseId # this is required for highlighting support as this is a major event
      viewerDidAuthor # this is required for giving the viewer's comments a different border color
      issue {
        author {
          login
        }
      }
      author {
        login
        avatarUrl
        profileUrl
      }
    }
    ... on CrossReferencedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      source {
        __typename
      }
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      willCloseTarget
    }
    ... on LabeledEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      label {
        id
      }
    }
    ... on UnlabeledEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      label {
        id
      }
    }
    ... on AssignedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      assignee {
        ... on User {
          id
        }
        ... on Bot {
          id
        }
        ... on Mannequin {
          id
        }
        ... on Organization {
          id
        }
      }
    }
    ... on UnassignedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      assignee {
        __typename
        ... on User {
          id
        }
        ... on Bot {
          id
        }
        ... on Mannequin {
          id
        }
        ... on Organization {
          id
        }
      }
    }
    ... on MilestonedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      milestone {
        id
      }
    }
    ... on DemilestonedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      milestone {
        id
      }
    }
    # Issue field events need issueField.name for rollup deduplication in timeline-rollups.ts
    # The fragment spreads below provide full data for rendering, but deduplication happens
    # before useFragment is called, so we need the name available at the timeline item level.
    ... on IssueFieldAddedEvent {
      # eslint-disable-next-line relay/unused-fields Required for rollup deduplication by field name
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
      }
    }
    ... on IssueFieldChangedEvent {
      # eslint-disable-next-line relay/unused-fields Required for rollup deduplication by field name
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
      }
    }
    ... on IssueFieldRemovedEvent {
      # eslint-disable-next-line relay/unused-fields Required for rollup deduplication by field name
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
      }
    }
    ... on IssueCommentPinnedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      issueComment {
        ... on IssueComment {
          id
        }
      }
    }
    ... on IssueCommentUnpinnedEvent {
      # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
      issueComment {
        ... on IssueComment {
          id
        }
      }
    }
    ...IssueComment_issueComment @dangerously_unaliased_fixme
    ...SubscribedEvent @dangerously_unaliased_fixme
    ...UnsubscribedEvent @dangerously_unaliased_fixme
    ...MentionedEvent @dangerously_unaliased_fixme
    ...ClosedEvent @dangerously_unaliased_fixme
    ...ReopenedEvent @dangerously_unaliased_fixme
    ...LockedEvent @dangerously_unaliased_fixme
    ...UnlockedEvent @dangerously_unaliased_fixme
    ...PinnedEvent @dangerously_unaliased_fixme
    ...UnpinnedEvent @dangerously_unaliased_fixme
    ...LabeledEvent @dangerously_unaliased_fixme
    ...RenamedTitleEvent @dangerously_unaliased_fixme
    ...UnlabeledEvent @dangerously_unaliased_fixme
    ...UnassignedEvent @dangerously_unaliased_fixme
    ...AssignedEvent @dangerously_unaliased_fixme
    ...BlockedByAddedEvent @dangerously_unaliased_fixme
    ...BlockedByRemovedEvent @dangerously_unaliased_fixme
    ...BlockingAddedEvent @dangerously_unaliased_fixme
    ...BlockingRemovedEvent @dangerously_unaliased_fixme
    ...CommentDeletedEvent @dangerously_unaliased_fixme
    ...UserBlockedEvent @dangerously_unaliased_fixme
    ...SubIssueAddedEvent @dangerously_unaliased_fixme
    ...SubIssueRemovedEvent @dangerously_unaliased_fixme
    ...ParentIssueAddedEvent @dangerously_unaliased_fixme
    ...ParentIssueRemovedEvent @dangerously_unaliased_fixme
    ...MilestonedEvent @dangerously_unaliased_fixme
    ...DemilestonedEvent @dangerously_unaliased_fixme
    ...CrossReferencedEvent @dangerously_unaliased_fixme
    ...ReferencedEvent @dangerously_unaliased_fixme
    ...ConnectedEvent @dangerously_unaliased_fixme
    ...TransferredEvent @dangerously_unaliased_fixme
    ...DisconnectedEvent @dangerously_unaliased_fixme
    ...MarkedAsDuplicateEvent @dangerously_unaliased_fixme
    ...UnmarkedAsDuplicateEvent @dangerously_unaliased_fixme
    ...ConvertedToDiscussionEvent @dangerously_unaliased_fixme
    ...AddedToProjectV2Event @dangerously_unaliased_fixme
    ...RemovedFromProjectV2Event @dangerously_unaliased_fixme
    ...ProjectV2ItemStatusChangedEvent @dangerously_unaliased_fixme
    ...ConvertedFromDraftEvent @dangerously_unaliased_fixme
    ...IssueTypeAddedEvent @dangerously_unaliased_fixme
    ...IssueTypeRemovedEvent @dangerously_unaliased_fixme
    ...IssueTypeChangedEvent @dangerously_unaliased_fixme
    ...IssueFieldChangedEvent @dangerously_unaliased_fixme
    ...IssueFieldAddedEvent @dangerously_unaliased_fixme
    ...IssueFieldRemovedEvent @dangerously_unaliased_fixme
    ...IssueCommentPinnedEvent @dangerously_unaliased_fixme
    ...IssueCommentUnpinnedEvent @dangerously_unaliased_fixme
  }
`

/**
 * Props exclusively propagated to render IssueComment and the other timeline event components.
 *
 * Not used by IssueTimelineItem itself.
 */
export type EventProps = {
  viewer: string | null
  onCommentChange: (id: string) => void
  onCommentReply: (quotedComment: string) => void
  onCommentEditCancel: (id: string) => void
  optionConfig: OptionConfig
}

export type IssueTimelineItemProps = {
  item: RolledUpTimelineItem<IssueTimelineItem$data>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  isHighlighted?: boolean
  addDivider?: boolean
  refAttribute?: RefObject<HTMLDivElement | null>
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
  showCopilotMenuItem?: boolean
  rationales?: Record<string, IssueEventRationales$data['eventRationales'][number]>
} & EventProps

/**
 * This component renders a single timeline item which can be a timeline event or a comment.
 *
 * It takes a rollup group item as it is intended to support rollup groups.
 */
export const IssueTimelineItem = ({
  item: {item, rollupGroup},
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  ownerLogin,
  issueUrl,
  isHighlighted,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  addDivider = false,
  optionConfig,
  refAttribute,
  secondaryTimelineQueryRef,
  showCopilotMenuItem = false,
  rationales,
}: IssueTimelineItemProps) => {
  const timelineConnectionIds = useTimelineConnectionIds()

  if (!item) return null

  const isMajorEvent = VALUES.timeline.majorEventTypes.includes(item.__typename)

  let itemToRender: ReactElement | null = null
  let commentParams: TimelineRowBorderCommentParams | undefined = undefined
  if (item.__typename === 'IssueComment') {
    commentParams = {
      first: false,
      last: false,
      viewerDidAuthor: item.viewerDidAuthor,
    }
    itemToRender = (
      <IssueComment
        comment={item}
        commentSubjectAuthorLogin={item.issue?.author?.login ?? ''}
        onChange={() => onCommentChange?.(item.__id)}
        onEditCancel={() => onCommentEditCancel?.(item.__id)}
        onReply={quote => onCommentReply?.(quote)}
        onSave={() => onCommentEditCancel?.(item.__id)}
        highlightedCommentId={isHighlighted ? String(item.databaseId) : undefined}
        navigate={optionConfig.navigate}
        refAttribute={refAttribute}
        onLinkClick={optionConfig.onLinkClick}
        showCopilotMenuItem={showCopilotMenuItem}
        relayConnectionIds={[timelineConnectionIds.front, timelineConnectionIds.back]}
      />
    )
  }

  const TimelineReactElement = TIMELINE_ITEMS[item.__typename]
  if (TimelineReactElement) {
    itemToRender = (
      <TimelineReactElement
        key={item.__id}
        queryRef={item}
        rollupGroup={rollupGroup}
        currentIssueId={issueId}
        issueUrl={issueUrl}
        repositoryId={repositoryId}
        repositoryNameWithOwner={repositoryNameWithOwner}
        ownerLogin={ownerLogin}
        highlightedEventId={isHighlighted ? String(item.databaseId) : undefined}
        timelineEventBaseUrl={optionConfig.timelineEventBaseUrl || '/issues'}
        refAttribute={refAttribute}
        onLinkClick={optionConfig.onLinkClick}
        viewer={viewer}
        secondaryTimelineQueryRef={secondaryTimelineQueryRef}
        rationales={rationales}
      />
    )
  }

  const wrappedElement = (
    <TimelineRowBorder
      key={item.__id}
      ref={refAttribute}
      item={item}
      addDivider={addDivider}
      isMajor={isMajorEvent && VALUES.timeline.borderedMajorEventTypes.includes(item.__typename)}
      isHighlighted={item.__typename === 'IssueComment' && isHighlighted}
      commentParams={commentParams}
    >
      {itemToRender}
    </TimelineRowBorder>
  )

  const leadingElement =
    item.__typename === 'IssueComment' ? <TimelineItemAvatar item={item} addDivider={addDivider} /> : undefined

  return wrapElement(wrappedElement, leadingElement, item.__id)
}

const TimelineItemAvatar = ({item, addDivider}: {item: IssueTimelineItem$data; addDivider: boolean}) => {
  const {avatarUrl, login, profileUrl} = item.author ?? VALUES.ghost
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login})

  return (
    <Link
      href={profileUrl ?? undefined}
      {...(profileUrl ? hovercardAttrs : {})}
      aria-label={`@${login}'s profile`}
      className={`${profileUrl ? styles.avatarLink : styles.avatarWithoutLink} ${styles.avatarOuter}`}
    >
      <GitHubAvatar
        key={login}
        alt={login}
        src={avatarUrl}
        size={40}
        className={`${styles.issueViewerAvatar} ${addDivider ? styles.avatarWithDivider : styles.avatarWithoutDivider}`}
      />
    </Link>
  )
}
